import AddRoundedIcon from "@mui/icons-material/Add";

import Button, { ButtonTypes } from "../../Button";
import { CardStackIllustration } from "../CardStackIllustration";
import { useResponsive } from "../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../utils/constants";
import {
  Actions,
  Description,
  Frame,
  PlusBadge,
  Title,
  Wrapper,
} from "./styles";
import { ChartEmptyStateProps } from "./types";

/**
 * Empty state shown when a chart-backed collection (savings, liabilities) has no
 * records yet. A card-stack illustration carries a mini-chart and a "+" badge,
 * with the headline, supporting copy and primary CTA framed inside a dashed
 * panel. The CTA goes full width on compact layouts to match the mobile design.
 */
export const ChartEmptyState: React.FC<ChartEmptyStateProps> = ({
  chart,
  title,
  description,
  buttonLabel,
  onAction,
  ariaLabel,
  secondaryActionLabel,
  onSecondaryAction,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const badge = (
    <PlusBadge>
      <AddRoundedIcon />
    </PlusBadge>
  );

  const secondaryAction =
    secondaryActionLabel && onSecondaryAction ? (
      <Button
        type={ButtonTypes.secondary}
        size="large"
        isFullWidth={isCompact}
        onClick={onSecondaryAction}
        aria-label={secondaryActionLabel}
      >
        {secondaryActionLabel}
      </Button>
    ) : null;

  return (
    <Wrapper role="region" aria-label={ariaLabel}>
      <Frame>
        <CardStackIllustration badge={badge}>{chart}</CardStackIllustration>

        <Title>{title}</Title>
        <Description>{description}</Description>

        <Actions>
          <Button
            type={ButtonTypes.primary}
            size="large"
            isFullWidth={isCompact}
            onClick={onAction}
            aria-label={buttonLabel}
          >
            <AddRoundedIcon fontSize="small" />
            {buttonLabel}
          </Button>
          {secondaryAction}
        </Actions>
      </Frame>
    </Wrapper>
  );
};
